import { useState } from "react";
import SectionHeader from "@/components/home/SectionHeader";
import Reveal from "@/components/home/Reveal";
import { HOME_WRAP } from "@/components/home/homeTheme";
import GlobalPresenceMap from "./GlobalPresenceMap";

/**
 * Verified numbers only: they come from the homepage CMS stats (Admin → Home Page → Stats) — the same
 * figures the trust bar under the hero shows. Nothing is invented here; a stat that isn't in the CMS is omitted.
 */
const STAT_MATCHERS = [
  { re: /countr/i, label: "Countries Served" },
  { re: /year/i, label: "Years of Experience" },
  { re: /customer/i, label: "Happy Customers" },
];

function pickStats(data) {
  const cms = (Array.isArray(data?.stats) ? data.stats : []).filter((s) => s.status !== false);
  const found = STAT_MATCHERS.map(({ re, label }) => {
    const s = cms.find((x) => re.test(String(x.title || "")));
    return s ? { value: `${s.number}${s.suffix || ""}`, label: s.title || label } : null;
  }).filter(Boolean);
  // The 45+ export claim is already stated across the site (header, footer, About) — safe as a fallback.
  return found.length ? found : [{ value: "45+", label: "Countries Served" }];
}

/**
 * Homepage "Global Presence" — the natural next step after Industries: industries we serve, then the
 * countries we serve. Same navy, so it reads as one continuous story; a hairline bridge names the link.
 */
const DEFAULT_DESCRIPTION_TEMPLATE = "Manufacturer and exporter of industrial tube, bolting, pipe preparation and maintenance tools{{countriesClause}}.";

export default function GlobalPresenceSection({ data }) {
  const [region, setRegion] = useState("all");
  const stats = pickStats(data);
  const countries = stats.find((s) => /countr/i.test(s.label));
  const section = data?.globalPresenceSection || {};
  const descriptionTemplate = section.descriptionTemplate || DEFAULT_DESCRIPTION_TEMPLATE;
  const description = descriptionTemplate.replace("{{countriesClause}}", countries ? `, supplying customers in ${countries.value} countries` : "");

  return (
    <section className="relative isolate overflow-hidden bg-[#071B3A] pb-20 pt-2 lg:pb-28 lg:pt-4" aria-labelledby="home-global-title" data-testid="home-global-presence">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#1769FF]/[0.14] blur-[150px]" />
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_70%_55%_at_20%_100%,#000,transparent)]" />
      </div>

      <div className={HOME_WRAP}>
        {/* bridge from the Industries section above */}
        <Reveal y={10}>
          <div className="mb-10 flex items-center justify-center gap-4 sm:gap-6 lg:mb-12" aria-hidden="true">
            <span className="hidden h-px flex-1 bg-gradient-to-r from-transparent to-white/20 sm:block" />
            <span className="whitespace-nowrap text-center text-[9.5px] font-semibold uppercase tracking-[0.16em] text-white/45 min-[400px]:text-[10px] sm:text-[11px] sm:tracking-[0.26em]">
              Industries we serve <span className="mx-1 text-[#3B82F6] sm:mx-2.5">+</span> Countries we serve
            </span>
            <span className="hidden h-px flex-1 bg-gradient-to-l from-transparent to-white/20 sm:block" />
          </div>
        </Reveal>

        <SectionHeader
          tone="dark"
          eyebrow={section.eyebrow || "Global Presence"}
          title={<><span className="block">{section.titleLine1 || "Engineering Solutions,"}</span><span className="block">{section.titleLine2 || "Delivered Worldwide."}</span></>}
          titleId="home-global-title"
          description={description}
          action={
            <dl className="grid overflow-hidden rounded-xl border border-white/10 bg-white/10 md:min-w-[400px]" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: "1px" }}>
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col-reverse justify-end bg-[#0A2148] px-4 py-4 sm:px-5">
                  <dt className="mt-1.5 text-[12px] leading-snug text-white/55">{s.label}</dt>
                  <dd className="font-heading text-[24px] font-semibold leading-none tracking-[-0.02em] text-white tabular-nums sm:text-[28px]">{s.value}</dd>
                </div>
              ))}
            </dl>
          }
          className="mb-10 lg:mb-12"
        />

        <Reveal y={20}>
          <GlobalPresenceMap variant="full" region={region} onRegionChange={setRegion} />
          <p className="mt-4 text-[12px] leading-relaxed text-white/40">
            Select a highlighted country for details. Drag to pan, use + / − to zoom
            <span className="hidden sm:inline"> (or Ctrl / ⌘ + scroll)</span><span className="sm:hidden"> or pinch</span>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
